import {Popup, RequestStatus, SearchSelectDropdown, SearchSelectOption, Skeleton, usePopup} from "../UIElements";
import '../scss/SupportNeedsView.scss';
import {Link} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {
    GetCodes,
    GetColumnConfig,
    GetContacts, GetFinishedAndInProgressSurveys, GetRequestComments, GetSelectOptions,
    GetSupportNeeds, GetSupportNeedsWithImpersonate, GetSupportNeedsWithUsagerequest,
    GetSurveys, GetSurveyWithId, GetUser,
    GetUsers,
} from "../API";
import {findUserById, formatDate, GetDifferenceText, getStudentFromContacts, renderUser} from "../formats";
import {FormattedMessage, useIntl} from "react-intl";
import {ReactComponent as Acorn} from "../svg/acorn.svg";
import {ReactComponent as Face} from "../svg/face.svg";
import _ from "lodash";
import {ReactComponent as CloseIcon} from "../svg/close.svg";
import {ReactComponent as FilterIcon} from "../svg/filter.svg";
import {useCurrentUserData} from "../App";
import {ReactComponent as InfoIcon} from "../svg/info.svg";
import {AssignedProviders, GetUserName} from "./SupportRequest";
import {ReactComponent as NoteIcon} from "../svg/note.svg";

export function SupportNeedsView() {
    const {currentUserData} = useCurrentUserData()
    const isSuperuser = currentUserData.superuser
    const isCoordinator = currentUserData.coordinator
    const superuserOrCoordinator = isSuperuser || isCoordinator
    // Show impersonated view on load. Personal requests not available to superuser
    const [impersonateId, setImpersonateId] = useState((superuserOrCoordinator && localStorage.getItem("supportProviderFilters")) ? (isSuperuser && localStorage.getItem("supportProviderFilters") === "MINE") ? "" : localStorage.getItem("supportProviderFilters") : "")
    const getSupportNeeds = impersonateId?.length > 0 ? isSuperuser ? GetSupportNeedsWithImpersonate(impersonateId) : GetSupportNeedsWithUsagerequest(impersonateId) : GetSupportNeeds()
    const getCodes = GetCodes()
    const getSurvey = GetFinishedAndInProgressSurveys()
    const loading = getSupportNeeds.status === "loading" || getCodes.status === "loading" || getSurvey.isLoading
    const error = getSupportNeeds.status === "error" || getCodes.status === "error" || getSurvey.isError
    const success = getSupportNeeds.status === "success" && getCodes.status === "success" && getSurvey.isSuccess
    const intl = useIntl()

    return <>
        <main className={"support-requests-wrapper"}>
            {loading &&
                <SupportRequestsSkeleton />
            }
            {error &&
                <>
                    {
                        getSupportNeeds.status === "error" ?
                        <p>
                            {intl.formatMessage({
                            id: 'error.supportRequests',
                            defaultMessage: 'Error in loading support requests',
                        })}
                        </p> :
                        <p>
                            {intl.formatMessage({
                            id: 'error.contacts',
                            defaultMessage: 'Error in loading contacts',
                        })}
                        </p>
                    }
                </>
            }
            {success &&
                <SupportRequests data={getSupportNeeds.data}
                                 codes={getCodes.data[0]}
                                 superuserOrCoordinator={superuserOrCoordinator}
                                 setSupportProviderFilter={e=>setImpersonateId(e)}
                                 supportProviderFilter={impersonateId}
                                 isSuperuser={isSuperuser}
                                 isImpersonating={impersonateId?.length > 0}
                                 surveysData={getSurvey?.data}
                />
            }
        </main>
    </>
}

function SupportRequests({data, surveysData, codes, superuserOrCoordinator, setSupportProviderFilter, supportProviderFilter, isSuperuser, isImpersonating}) {
    const intl = useIntl()
    const getColumnConfig = GetColumnConfig()

    const noSupportRequests = data.length < 1 && !isImpersonating
    const [studentMetaFilter, setStudentMetaFilter] = useState(localStorage.getItem("supportRequestFilters") ? JSON.parse(localStorage.getItem("supportRequestFilters")) : [])
    const [anyOrAll, setAnyOrAll] = useState(localStorage.getItem("anyOrAllFilters") ? localStorage.getItem("anyOrAllFilters") : "ALL")
    const [campaignFilter, setCampaignFilter] = useState(localStorage.getItem("campaignFilters") ? JSON.parse(localStorage.getItem("campaignFilters")) : [])
    const hasCampaignFilter = campaignFilter.length > 0 && campaignFilter.some(campaign => campaign?.surveyId || campaign?.campaign)
    const hasStudentMetaFilter = studentMetaFilter.length > 0 && studentMetaFilter.some(filter => filter?.value)
    const hasFilters = hasStudentMetaFilter || hasCampaignFilter || (supportProviderFilter.length > 0)

    const removeFilters = () => {
        localStorage.removeItem("supportRequestFilters")
        localStorage.removeItem("anyOrAllFilters")
        localStorage.removeItem("supportProviderFilters")
        localStorage.removeItem("campaignFilters")
        setStudentMetaFilter([])
        setCampaignFilter([])
        setSupportProviderFilter("")
    }

    const getFilteredData = (data) => {
        const filterDataByCampaign = (data) => {
            if (!hasCampaignFilter) return data

            const filterSurveyIds = campaignFilter
                .filter(filter => filter?.surveyId)
                .map(filter => filter.surveyId)

            const filterCampaignNames = campaignFilter
                .filter(filter => filter?.campaign)
                .map(filter => filter.campaign)

            return data.filter(request => {
                if (filterSurveyIds.includes(request?.survey)) {
                    return true
                }

                const survey = surveysData.find(survey => survey.id === request.survey)
                if (survey && filterCampaignNames.includes(survey?.config?.campaign)) {
                    return true;
                }

                return false
            })
        }

        const filterDataByStudentMeta = (data) => {
            if (!hasStudentMetaFilter) return data

            return data.filter(request => {
                const matches = studentMetaFilter.filter(filter =>
                    filter.value && filter.meta && request.contactmeta?.[filter.meta] === filter.value
                )

                // If "ALL" criteria is specified, a request must match all non-null filters
                if (anyOrAll === "ALL") {
                    const countOfValidFilters = studentMetaFilter.filter(filter => filter?.meta && filter?.value).length
                    return matches.length === countOfValidFilters
                }

                // Default ("ANY"), at least one match is enough
                return matches.length > 0
            })
        }

        let result = filterDataByCampaign(data)
        result = filterDataByStudentMeta(result)

        return _.uniqBy(result, 'id')
    }

    const filteredData = hasFilters ? getFilteredData(data) : data
    const [searchValue, setSearchValue] = useState(sessionStorage.getItem("searchValue") ? sessionStorage.getItem("searchValue") : "")
    const [displaySearchValue, setDisplaySearchValue] = useState(searchValue)
    const [searchIsFocused, setSearchIsFocused] = useState(false)
    const ackedData =  filteredData.filter(e => e.status === "ACKED" || e.status === "ERROR")
    const newOrOpenedData =  filteredData.filter(e => e.status === "NEW" || e.status === "OPENED")
    const sortedAckedData = ackedData.sort((a,b) => new Date(formatDate(b.updated)) - new Date(formatDate(a.updated)))
    const sortedNewOrOpenedData = newOrOpenedData.sort((a,b) => new Date(formatDate(b.updated)) - new Date(formatDate(a.updated)))
    const sortedData = sortedNewOrOpenedData.concat(sortedAckedData)
    sortedData.forEach(request => {
        request.studentName = (request?.contactmeta.hasOwnProperty("firstname") && request?.contactmeta.hasOwnProperty("lastname")) ?
            request.contactmeta.firstname + " " + request.contactmeta.lastname
            : request?.contactmeta ?
                intl.formatMessage({id:"missingName", defaultMessage:"Missing name"})
                : intl.formatMessage({id:"dataExpired", defaultMessage:"Student info expired"})
        request.categoryName = codes.hasOwnProperty(request.category) ? codes[request.category]["title"] : null
    })

    const searchData = sortedData.filter(request => {
        if (request.studentName.toString().toLowerCase().includes(searchValue.toLowerCase())) {
            return true
        }
        if (request.hasOwnProperty("categoryName") && request.categoryName && request.categoryName.toString().toLowerCase().includes(searchValue.toLowerCase())) {
            return true
        }
        if (request.hasOwnProperty("lastcomment") && request.lastcomment && request.lastcomment.toString().toLowerCase().includes(searchValue.toLowerCase())) {
            return true
        }
        if (request.hasOwnProperty("contactmeta")) {
            for (let metaValue of Object.values(request.contactmeta)) {
                if (metaValue && metaValue.toString().toLowerCase().includes(searchValue.toLowerCase())) {
                    return true
                }
            }
        }
        return false
    })

    const allDataOpenOrNew = data.filter(e => e.status === "NEW" || e.status === "OPENED")
    const allDataNoOpenRequests = data.filter(e => e.status === "NEW" || e.status === "OPENED").length < 1
    const [noOpenRequests, setNoOpenRequests] = useState(allDataNoOpenRequests)
    const noDataWithFilters = hasFilters && filteredData.length < 1

    useEffect(() => {
        if (allDataNoOpenRequests) {
            document.title = intl.formatMessage({
                    id:"requests",
                    defaultMessage:"Requests | Annie Advisor",
                }, {
                    n: newOrOpenedData.length
                }
            )
            setNoOpenRequests(true)
        } else {
            document.title = intl.formatMessage({
                    id:"NRequests",
                    defaultMessage:"({n}) Requests | Annie Advisor",
                }, {
                    n: allDataOpenOrNew.length
                }
            )
            setNoOpenRequests(false)
        }

    }, [allDataNoOpenRequests, intl])

    const setScrollPositionToSessionStorage = () => {
        sessionStorage.setItem("scrollPosition", window.pageYOffset.toString())
    }

    const filteredAmountForSearch = filteredData?.length > 0 ? ' ('+filteredData.length+')' : ""
    const ackedAmount =  filteredData.filter(e => e.status === "ACKED")?.length
    const errorAmount = filteredData.filter(e => e.status === "ERROR")?.length
    const newAmount = filteredData.filter(e => e.status === "NEW")?.length
    const openAmount = filteredData.filter(e => e.status === "OPENED")?.length
    const amountForSearchTitle = filteredData?.length > 0 ?
        (newAmount ? (newAmount + " " + intl.formatMessage({id:"new",defaultMessage:"New"}) + "\n") : "" )+
        (openAmount ? (openAmount + " " + intl.formatMessage({id:"open",defaultMessage:"Open"}) + "\n") : "" )+
        (errorAmount ? (errorAmount + " " + intl.formatMessage({id:"error",defaultMessage:"Error"}) + "\n") :"")+
        (ackedAmount ? (ackedAmount + " " + intl.formatMessage({id:"seen",defaultMessage:"Seen"})) : "")
        : ""

    const searchAmount = ` (${searchData?.length})`

    useEffect(()=>{
        if (!searchIsFocused && searchValue) {
            setDisplaySearchValue(`${searchValue}${searchAmount}`)
        } else {
            setDisplaySearchValue(searchValue)
        }
    },[searchIsFocused, searchValue, searchAmount])

    const handleSearchFocus = () => {
        setSearchIsFocused(true)
        setDisplaySearchValue(searchValue)
    }

    const handleSearchBlur = () => {
        setSearchIsFocused(false)
        if (searchValue) {
            setDisplaySearchValue(`${searchValue}${searchAmount}`)
        }
    }

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value)
        setDisplaySearchValue(e.target.value)
        sessionStorage.setItem("searchValue", e.target.value)
    }

    return <div className={"support-requests-container"}>
        {noOpenRequests && !noSupportRequests && !noDataWithFilters &&
        <div className={"no-requests-container"}>
            <div id={"no-open"}>
               <Acorn />
            </div>
            <h4>
                <FormattedMessage
                    id={"noOpenRequests"}
                    defaultMessage={"No open support requests!{br}All done for now."}
                    values={{br: <br/>}}/>
            </h4>
        </div>
        }
        {!noSupportRequests &&
            <>
                <div className={"search-and-filter"}>
                    <input type={"search"} value={displaySearchValue} title={amountForSearchTitle} autoFocus={sessionStorage.getItem("searchValue")}
                           onChange={handleSearchChange}
                           onFocus={handleSearchFocus}
                           onBlur={handleSearchBlur}
                           placeholder={
                               intl.formatMessage({
                                   id: 'search',
                                   defaultMessage: 'Search',
                               }) + searchAmount
                           } />
                    <FilterRequests storageFilters={studentMetaFilter}
                                    setStorageFilters={filters => setStudentMetaFilter(filters)}
                                    storageAnyOrAll={anyOrAll}
                                    setStorageAnyOrAll={anyOrAll => setAnyOrAll(anyOrAll)}
                                    storageSupportProviderFilter={supportProviderFilter}
                                    setStorageSupportProviderFilter={filter => setSupportProviderFilter(filter)}
                                    storageCampaignFilter={campaignFilter}
                                    setStorageCampaignFilter={filter => setCampaignFilter(filter)}
                                    hasFilters={hasFilters}
                                    superuserOrCoordinator={superuserOrCoordinator}
                                    isSuperuser={isSuperuser}
                                    getColumnConfig={getColumnConfig}
                                    requests={data}
                                    surveysData={surveysData}
                    />
                    {hasFilters &&
                        <>
                            <button className={"text-button"} onClick={()=>removeFilters()}>
                                <CloseIcon />{intl.formatMessage({id:"removeFilter",defaultMessage:"Remove filter"})}:
                                <FiltersText
                                    superuserOrCoordinator={superuserOrCoordinator}
                                    supportProviderFilter={supportProviderFilter}
                                    hasCampaignFilter={hasCampaignFilter}
                                    campaignFilter={campaignFilter}
                                    hasStudentMetaFilter={hasStudentMetaFilter}
                                    studentMetaFilter={studentMetaFilter}
                                />
                            </button>
                        </>
                    }
                </div>
                {noDataWithFilters &&
                <div className={"no-requests-container"}>
                    <h4>
                        <FormattedMessage
                            id={"noDataWithFilters"}
                            defaultMessage={"No results with filters.{br}Remove filters to see all requests."}
                            values={{br: <br/>}}/>
                    </h4>
                </div>
                }
                <div className={"support-requests"}>
                    {searchData.length < 1 && searchValue.length > 0 &&
                    <h2>
                        {intl.formatMessage({
                            id: 'noSearchResults',
                            defaultMessage: 'No search results',
                        })}
                    </h2>
                    }
                    {searchData.map((request, i) => {
                        const deactive = request.status === "ACKED" || request.status === "ERROR"
                        const category = request.categoryName ? request.categoryName : intl.formatMessage({
                            id: 'missingNameForCategory',
                            defaultMessage: 'Name missing for support category',
                        })
                        const requestId = request.id
                        const requestIdString = requestId.toString()
                        const followupStatus = request.hasOwnProperty("followupstatus") && request.followupstatus ? request.followupstatus : ""

                        return <Link to={"request/"+requestIdString} state={{direction:'left',animation:'true'}} onClick={()=>setScrollPositionToSessionStorage()} key={i} className={deactive ? "support-request deactive" : "support-request"}>
                            <div className={"request-name"}>
                                <RequestStatus status={request.status} followup={followupStatus} survey={request?.survey}/>
                                <h2>{request.studentName}</h2>
                                <h3>{category}
                                </h3>
                                <LatestComment request={request}/>
                            </div>
                            <RequestInfo request={request} />
                        </Link>
                    })}
                </div>
            </>
        }
        {noSupportRequests &&
        <div className={"no-requests-container"}>
            <div id={"no-requests"}>
                <Face />
            </div>
            <h4>
                <FormattedMessage
                    id={"noRequests"}
                    defaultMessage={"You don't have any support requests right now.{br}We'll send an email when a request arrives."}
                    values={{br: <br/>}}/>
            </h4>
        </div>
        }
    </div>
}

function FiltersText({superuserOrCoordinator, supportProviderFilter, hasCampaignFilter, campaignFilter, hasStudentMetaFilter, studentMetaFilter}) {
    const intl = useIntl()
    const getMetaName = meta => intl.formatMessage({
        defaultMessage: meta,
        id: meta
    })
    const [providerId, setProviderId] = useState(null)
    const { data: userData, isSuccess: userIsSuccess } = GetUser(providerId, providerId !== null);

    useEffect(() => {
        if (superuserOrCoordinator && supportProviderFilter.length > 0 && supportProviderFilter !== "MINE") {
            setProviderId(supportProviderFilter)
        } else {
            setProviderId(null)
        }
    }, [superuserOrCoordinator, supportProviderFilter])

    let filterTexts = [];

    const providerFilterText = (superuserOrCoordinator && supportProviderFilter.length > 0) ?
        (supportProviderFilter === "MINE" ? intl.formatMessage({ id: 'myRequests', defaultMessage: 'My Support Requests' }) :
            (userIsSuccess && userData ? renderUser(userData[0]) : intl.formatMessage({id:'loading',defaultMessage:'Loading...'}))) : null

    const campaignFilterText = hasCampaignFilter ? campaignFilter.filter(filter => filter?.surveyId || filter?.campaign).map(campaign => campaign.title).join(", ") : null
    const studentFilterText = hasStudentMetaFilter ? studentMetaFilter.filter(filter => (filter?.value && filter?.meta)).map(student => `${getMetaName(student.meta)}: ${student.value}`).join(", ") : null

    if (providerFilterText) filterTexts.push(providerFilterText)
    if (campaignFilterText) filterTexts.push(campaignFilterText)
    if (studentFilterText) filterTexts.push(studentFilterText)

    const filtersText = filterTexts.join(", ")

    return <span className={"overflow-ellipsis filter-text"} title={filtersText}>{filtersText}</span>
}

export function LatestComment({request}) {

    if (!request.hasOwnProperty("lastcomment") || !request.lastcomment?.length > 0) {
        return null
    }

    return <div className={"latest-comment"}>
        <NoteIcon />
        <span>{request.lastcomment}</span>
    </div>
}

function FilterRequests({storageFilters, storageAnyOrAll, setStorageFilters, setStorageAnyOrAll, storageSupportProviderFilter, storageCampaignFilter, setStorageCampaignFilter, setStorageSupportProviderFilter, hasFilters, superuserOrCoordinator, isSuperuser, getColumnConfig, requests, surveysData}) {
    const [popupOpen, setPopupOpen] = useState(false)
    const filterObject = {
        id: 1,
        meta: null,
        value: null
    }
    const campaignObject = {
        id: 1,
        campaign: null,
        surveyId: null,
        title: null
    }
    const [filters, setFilters] = useState(storageFilters && storageFilters?.length > 0 ? storageFilters : [filterObject])
    const [anyOrAll, setAnyOrAll] = useState(storageAnyOrAll ? storageAnyOrAll : "ALL")
    const [supportProviderFilter, setSupportProviderFilter] = useState(superuserOrCoordinator && storageSupportProviderFilter ? storageSupportProviderFilter : "")
    const [campaignFilter, setCampaignFilter] = useState(storageCampaignFilter && storageCampaignFilter?.length > 0 ? storageCampaignFilter : [campaignObject])

    const columnConfig = getColumnConfig.isSuccess ? getColumnConfig?.data[0] : []
    const contactMetaValues = getColumnConfig.isSuccess ? Object.keys(columnConfig).filter(key => columnConfig[key]?.appVisible).sort((a,b)=>columnConfig[a]?.order - columnConfig[b]?.order)  : []

    const createUniqueId = (array, i) => {
        if (array.some(e => e.id === i)) {
            return createUniqueId(array, i + 1)
        }
        return i
    }

    const addFilter = () => {
        let newFilters = [...filters]
        let newObject = _.cloneDeep(filterObject)
        newObject.id = createUniqueId(filters, filters.length + 1)
        newFilters.push(newObject)
        setFilters(newFilters)
    }

    const removeFilter = (filterIndex) => {
        if (filters.length > 1) {
            let newFilters = [...filters]
            newFilters.splice(filterIndex, 1)
            setFilters(newFilters)
        } else {
            setFilters([filterObject])
        }
    }

    const updateFilters = filter => {
        let updatedFilters = [...filters]
        const index = updatedFilters.findIndex(e => e.id === filter.id)
        updatedFilters[index] = filter
        setFilters(updatedFilters)
    }

    const saveFilters = () => {
        localStorage.setItem("supportRequestFilters", JSON.stringify(filters))
        localStorage.setItem("anyOrAllFilters", anyOrAll)
        localStorage.setItem("supportProviderFilters", supportProviderFilter)
        localStorage.setItem("campaignFilters", JSON.stringify(campaignFilter))
        setStorageFilters(filters)
        setStorageAnyOrAll(anyOrAll)
        setStorageSupportProviderFilter(supportProviderFilter)
        setStorageCampaignFilter(campaignFilter)
        setPopupOpen(false)
    }

    const updateCampaignFilter = filter => {
        let updatedFilters = [...campaignFilter]
        const index = updatedFilters.findIndex(e => e.id === filter.id)
        updatedFilters[index] = filter
        setCampaignFilter(updatedFilters)
    }

    const addCampaignFilter = () => {
        let newCampaignFilter = [...campaignFilter]
        let newObject = _.cloneDeep(campaignObject)
        newObject.id = createUniqueId(campaignFilter, campaignFilter.length + 1)
        newCampaignFilter.push(newObject)
        setCampaignFilter(newCampaignFilter)
    }

    const removeCampaignFilter = (filterIndex) => {
        if (campaignFilter.length > 1) {
            let newFilters = [...campaignFilter]
            newFilters.splice(filterIndex, 1)
            setCampaignFilter(newFilters)
        } else {
            setCampaignFilter([campaignObject])
        }
    }

    const removeProviderFilter = () => {
        setSupportProviderFilter("")
    }

    const resetFilters = () => {
        setFilters([filterObject])
        setCampaignFilter([campaignObject])
        setSupportProviderFilter("")
        setAnyOrAll("ALL")
    }
    const intl = useIntl()

    return <>
        <button className={hasFilters ? "basic active" : "basic"} onClick={()=>setPopupOpen(true)}>
            <FilterIcon />
                {hasFilters ?
                    intl.formatMessage({
                        id: 'editFilter',
                        defaultMessage: 'Edit filter',
                    }) :
                    intl.formatMessage({
                        id: 'setFilter',
                        defaultMessage: 'Set filter',
                    })
                }
        </button>
        {popupOpen &&
            <Popup
                onClose={()=>setPopupOpen(false)}
                header={intl.formatMessage({
                    id: 'filter',
                    defaultMessage: 'Filter',
                })}
                className={"filters"}
            >
                {superuserOrCoordinator &&
                    <>
                        <h2>
                            {intl.formatMessage({
                                id: 'filterBySupportProvider',
                                defaultMessage: 'By Support Provider',
                            })}
                        </h2>
                        <SupportProviderSelector supportProviderFilter={supportProviderFilter}
                                                 setSupportProviderFilter={provider=>setSupportProviderFilter(provider)}
                                                 removeFilter={()=>removeProviderFilter()}
                                                 isSuperuser={isSuperuser}
                        />
                        <hr/>
                    </>
                }
                <h2>
                    {intl.formatMessage({
                        id: 'filterByCampaign',
                        defaultMessage: '' +
                            'By Campaign',
                    })}
                </h2>
                {campaignFilter.map((filter, i) => {
                    return <CampaignSelector key={filter.id}
                                             campaignFilter={filter}
                                             setCampaignFilter={campaign => updateCampaignFilter(campaign)}
                                             removeFilter={()=>removeCampaignFilter(i)}
                                             allFilters={campaignFilter}
                                             surveysData={surveysData}
                    />
                })}
                {
                    (campaignFilter[campaignFilter.length - 1]?.surveyId || campaignFilter[campaignFilter.length - 1]?.campaign) &&
                    <button className={"add-filter"} onClick={() => addCampaignFilter()}> {intl.formatMessage({
                        id: 'addCampaign',
                        defaultMessage: '+ add campaign',
                    })}</button>
                }
                <hr/>
                <h2>
                    {intl.formatMessage({
                        id: 'filterByStudentInformation',
                        defaultMessage: 'By Student Information',
                    })}
                </h2>
                {filters.map((filter, i) => {
                  return <StudentMetaFilter key={filter.id}
                                 removeFilter={()=>removeFilter(i)}
                                 filter={filter}
                                 contactMetaValues={contactMetaValues}
                                 getColumnConfig={getColumnConfig}
                                 updateFilters={filter => updateFilters(filter)}
                                 requests={requests}
                  />
                })}
                {
                    filters[filters.length - 1]?.meta &&
                    <button className={"add-filter"} onClick={() => addFilter()}>{intl.formatMessage({
                        id: 'addFilter',
                        defaultMessage: '+ add filter',
                    })}</button>
                }
                {filters?.length > 1 &&
                <p>
                    {intl.formatMessage({
                        id: 'match',
                        defaultMessage: 'Match',
                    })} <select className={"filter-select"} value={anyOrAll} onChange={e => {setAnyOrAll(e.target.value)}}>
                    <option value={"ALL"}>{intl.formatMessage({
                        id: 'allFilters',
                        defaultMessage: 'all rules',
                    })}</option>
                    <option value={"ANY"}>{intl.formatMessage({
                        id: 'anyFilters',
                        defaultMessage: 'any rule',
                    })}</option>
                </select>
                </p>
                }

                <div className={"options"}>
                    <button onClick={()=>resetFilters()}>{intl.formatMessage({
                        id: 'resetFilter',
                        defaultMessage: 'Reset filter',
                    })}</button>
                    <button onClick={()=>setPopupOpen(false)}>{intl.formatMessage({
                        id: 'cancel',
                        defaultMessage: 'Cancel',
                    })}</button>
                    <button className="select" onClick={()=>saveFilters()}>{intl.formatMessage({
                        id: 'saveFilter',
                        defaultMessage: 'Save filter',
                    })}</button>
                </div>
            </Popup>
        }
    </>
}

function CampaignSelector({campaignFilter, setCampaignFilter, removeFilter, allFilters, surveysData}) {
    const getFilterOptions = GetSelectOptions()
    const [searchSelectValue, setSearchSelectValue] = useState("")
    const intl = useIntl()
    const updateFilter = selectedValue => {
        const updatedFilter = {
            id: campaignFilter?.id,
            campaign: selectedValue.type === 'campaign' ? selectedValue.value : null,
            surveyId: selectedValue.type === 'survey' ? selectedValue.value : null,
            title: selectedValue.label
        };
        setCampaignFilter(updatedFilter);
    }
    const [renderAmount, setRenderAmount] = useState(100)
    const [allRendered, setAllRendered] = useState(false)
    const scrolledToBottom = () => {
        if (!allRendered) {
            setRenderAmount(renderAmount + 50)
            if (surveysData.length <= renderAmount) {
                setAllRendered(true)
            }
        }
    }

    return <SearchSelectDropdown value={campaignFilter?.title}
                                 placeholder={intl.formatMessage({
                                     id: 'selectCampaign',
                                     defaultMessage: 'Select Campaign',
                                 })}
                                 setSearchSelectValue={value=>setSearchSelectValue(value)}
                                 removeFilter={()=>removeFilter()}
                                 openOnRender={campaignFilter?.id > 1}
                                 scrolledToBottom={()=>scrolledToBottom()}
    >

        {getFilterOptions.isLoading &&
            <SearchSelectOption value={"LOADING"} disabled={true}>{intl.formatMessage({
                id: 'loadingCampaigns',
                defaultMessage: 'Loading campaigns...',
            })}</SearchSelectOption>
        }
        {getFilterOptions.isSuccess &&
            <CampaignFilterOptions
                filterOptions={getFilterOptions?.data}
                searchSelectValue={searchSelectValue}
                renderAmount={renderAmount}
                surveysData={surveysData}
                updateValue={value=>updateFilter(value)}
                allFilters={allFilters}
                campaignFilter={campaignFilter}
            />
        }
    </SearchSelectDropdown>
}

function CampaignFilterOptions({filterOptions, searchSelectValue, renderAmount, surveysData, updateValue, allFilters, campaignFilter}) {
    const getLatestEndTimeForCampaign = (campaignName) => {
        const relatedSurveys = surveysData.filter(survey => survey?.config?.campaign === campaignName)
        if (relatedSurveys.length === 0) return null
        return relatedSurveys.reduce((latest, survey) => {
            return latest > survey?.endtime ? latest : survey?.endtime
        }, relatedSurveys[0]?.endtime)
    }

    const campaignData = filterOptions?.campaign ? filterOptions?.campaign.filter(campaign => campaign.trim() !== "") : []
    const campaignOptions = campaignData.map((campaign) => ({
        value: campaign,
        label: campaign,
        endtime: getLatestEndTimeForCampaign(campaign),
        type: 'campaign'
    }))

    const surveyOptions = filterOptions?.survey?.map((surveyId) => {
        const survey = surveysData.find(obj => obj.id === surveyId)
        return {
            value: surveyId,
            label: survey?.config?.title || surveyId,
            endtime: survey ? survey.endtime : null,
            type: 'survey'
        }
    }) || []

    const combinedOptions = [...campaignOptions, ...surveyOptions].sort((a, b) => {
        if (a.endtime < b.endtime) return 1
        if (a.endtime > b.endtime) return -1
        return a.label.localeCompare(b.label)
    })

    const activeIds = allFilters.map(filter => filter?.surveyId || filter?.campaign)

    return <>
        {combinedOptions
            .filter(option => option?.label.toString().toLowerCase().includes(searchSelectValue.toLowerCase()))
            .slice(0, renderAmount)
            .map((option, i ) => {
                const isActive = activeIds.includes(option.value);
                let isDisabled = false;

                if (isActive) {
                    const activeFilter = allFilters.filter(filter => filter?.id === campaignFilter?.id);
                    if ((option.type === "survey" && activeFilter[0]?.surveyId !== option.value) ||
                        (option.type === "campaign" && activeFilter[0]?.campaign !== option.value)) {
                        isDisabled = true;
                    }
                }

                return (
                    <SearchSelectOption
                        key={i}
                        value={option}
                        setSelectValue={value => updateValue(value)}
                        active={isActive}
                        disabled={isDisabled}
                    >
                        {option?.label}
                    </SearchSelectOption>
        )})}
    </>
}

function SupportProviderSelector({supportProviderFilter, setSupportProviderFilter, removeFilter, isSuperuser}) {
    const getUsers = GetUsers()
    const [searchSelectValue, setSearchSelectValue] = useState("")
    const [renderAmount, setRenderAmount] = useState(100)
    const [allRendered, setAllRendered] = useState(false)
    const intl = useIntl()
    const scrolledToBottom = () => {
        if (!allRendered) {
            setRenderAmount(renderAmount + 50)
            if (getUsers.isSuccess && getUsers.data.length <= renderAmount) {
                setAllRendered(true)
            }
        }
    }

    const dropdownValue = getUsers.isSuccess ? findUserById(supportProviderFilter, getUsers?.data, intl) : supportProviderFilter

    return <SearchSelectDropdown value={supportProviderFilter === "MINE" ? intl.formatMessage({id: 'myRequests', defaultMessage: 'My Support Requests'}) : dropdownValue} placeholder={intl.formatMessage({
        id: 'selectSupportProvider',
        defaultMessage: 'Select Support Provider',
    })} setSearchSelectValue={value=>setSearchSelectValue(value)} removeFilter={()=>removeFilter()} scrolledToBottom={()=>scrolledToBottom()}>
        {!isSuperuser &&
        <SearchSelectOption value={"MINE"} active={supportProviderFilter === "MINE"} setSelectValue={value=>setSupportProviderFilter(value)}>{intl.formatMessage({
            id: 'myRequests',
            defaultMessage: 'My Support Requests',
        })}</SearchSelectOption>
        }
        {getUsers.isLoading &&
        <SearchSelectOption value={"LOADING"} disabled={true}>{intl.formatMessage({
            id: 'loadingProviders',
            defaultMessage: 'Loading Support Providers...',
        })}</SearchSelectOption>
        }
        {getUsers.isSuccess &&
        getUsers.data
            .filter(user => {
                const lowercaseSearchValue = searchSelectValue.toLowerCase()
                const fieldsToCheck = [
                    user?.meta?.firstname,
                    user?.meta?.lastname,
                    user?.meta?.role,
                    user?.meta?.email,
                    user?.meta?.userid,
                ]
                return fieldsToCheck.some(field => field?.toString().toLowerCase().includes(lowercaseSearchValue))
            })
            .slice(0, renderAmount)
            .map((user, i) => {
                const userName = renderUser(user)

                return <SearchSelectOption key={i} value={user?.id} setSelectValue={value=>setSupportProviderFilter(value)} active={supportProviderFilter === user?.id}>{userName}</SearchSelectOption>
        })
        }
    </SearchSelectDropdown>
}

function StudentMetaFilter({removeFilter, filter, contactMetaValues, getColumnConfig, updateFilters, requests}) {
    const intl = useIntl()
    const [searchMetaValue, setSearchMetaValue] = useState("")
    const [searchValue, setSearchValue] = useState("")

    const updateFilter = (meta, value) => {
        const filterUpdate = {
            id: filter.id,
            meta: meta,
            value: value
        }
        updateFilters(filterUpdate)
    }

    const getMetaName = meta => {
        if (meta) {
            return intl.formatMessage({
                defaultMessage: meta,
                id: meta
            })
        } else {
            return meta
        }
    }

    const metaValuesSet = requests.reduce((acc, request) => {
        if (filter?.meta && request?.contactmeta && request.contactmeta[filter.meta] !== undefined) {
            acc.add(request.contactmeta[filter.meta])
        }
        return acc
    }, new Set())

    const metaValues = Array.from(metaValuesSet)

    const [renderAmountForMeta, setRenderAmountForMeta] = useState(100)
    const [allRenderedForMeta, setAllRenderedForMeta] = useState(false)
    const scrolledToBottomForMeta = () => {
        if (!allRenderedForMeta) {
            setRenderAmountForMeta(renderAmountForMeta + 50)
            if (contactMetaValues && contactMetaValues.length <= renderAmountForMeta) {
                setAllRenderedForMeta(true)
            }
        }
    }

    const [renderAmountForValue, setRenderAmountForValue] = useState(100)
    const [allRenderedForValue, setAllRenderedForValue] = useState(false)
    const scrolledToBottomForValue = () => {
        if (!allRenderedForValue) {
            setRenderAmountForValue(renderAmountForValue + 50)
            if (metaValues && metaValues.length <= renderAmountForValue) {
                setAllRenderedForValue(true)
            }
        }
    }

    return <div className={"filter-container"}>
        <SearchSelectDropdown value={getMetaName(filter?.meta)} placeholder={intl.formatMessage({
            id: 'selectInformation',
            defaultMessage: 'Select Information',
        })} setSearchSelectValue={value=>setSearchMetaValue(value)} scrolledToBottom={()=>scrolledToBottomForMeta()}>
            {
                contactMetaValues.filter(value => value.toString().toLowerCase().includes(searchMetaValue.toLowerCase()) || getMetaName(value).toString().toLowerCase().includes(searchMetaValue.toLowerCase())
                ).slice(0, renderAmountForMeta).map((meta, i)=>{
                    return <SearchSelectOption key={i} value={meta} active={filter?.meta === meta} setSelectValue={meta => {
                        updateFilter(meta, filter?.value)
                    }}>{getMetaName(meta)}</SearchSelectOption>
                })
            }
        </SearchSelectDropdown>
        <SearchSelectDropdown value={filter?.value} placeholder={intl.formatMessage({
            id: 'selectValue',
            defaultMessage: 'Select Value',
        })} setSearchSelectValue={value=>setSearchValue(value)} disabled={!filter?.meta}  scrolledToBottom={()=>scrolledToBottomForValue()}>
            {
                metaValues.filter(value => value && value.toString().toLowerCase().includes(searchValue.toLowerCase())
                ).slice(0, renderAmountForValue).map((value, i)=>{
                    return <SearchSelectOption key={i} value={value} active={filter?.value === value} setSelectValue={value => {
                        updateFilter(filter?.meta, value)
                    }}>{value}</SearchSelectOption>
                })
            }
        </SearchSelectDropdown>
        {filter?.meta &&
        <button onClick={()=>removeFilter()} className={"icon-button"}>
            <CloseIcon />
        </button>
        }
    </div>
}

export function RequestInfo({request}) {
    const intl = useIntl()
    const updated = new Date(formatDate(request.updated)).toLocaleString(intl.locale, {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute:'2-digit'})
    const dateDifference = new Date() - new Date(formatDate(request.updated))
    const differenceText = GetDifferenceText(dateDifference)

    return <div className={"updated"}>
        <p>{differenceText}</p>
        <p>{updated}</p>
        <RequestInfoButton request={request} />
    </div>
}

function RequestInfoButton({request}) {
    const { showPopup } = usePopup()

    return <>
        <button className="info-button" onClick={(e)=> {
            e.stopPropagation()
            e.preventDefault()
            showPopup(<InfoPopup request={request}/>,'','Info')
        }}><InfoIcon/>Info</button>
    </>
}

function InfoPopup({request}) {
    const intl = useIntl()
    const getSurvey = GetSurveyWithId(request.survey)
    const getColumnConfig = GetColumnConfig()
    const {currentUserData} = useCurrentUserData()
    const isSuperuser = currentUserData.superuser
    const isCoordinator = currentUserData.coordinator
    const superuserOrCoordinator = isSuperuser || isCoordinator
    const surveyDate = getSurvey.isSuccess ?
        new Date(formatDate(getSurvey.data[0]?.starttime)).toLocaleString(intl.locale, {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute:'2-digit'}) + " – " + new Date(formatDate(getSurvey.data[0]?.endtime)).toLocaleString(intl.locale, {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute:'2-digit'})
        : ""

    return <>
        <h4>
            {intl.formatMessage({
                id:"campaign",
                defaultMessage:"Campaign"
            })}
        </h4>
        {getSurvey.isSuccess &&
            <CampaignInfo surveyDate={surveyDate} data={getSurvey.data[0]} />
        }
        {superuserOrCoordinator &&
            <>
                <hr />
                <h4>
                    {intl.formatMessage({
                        id:"assignedTo",
                        defaultMessage:"Assigned to"
                    })}
                </h4>
                <AssignedProviders request={request} />
            </>
        }
        <hr />
        <h4>
            {intl.formatMessage({
                id:"studentInfo",
                defaultMessage:"Student Information"
            })}
        </h4>
        {getColumnConfig.isSuccess &&
            <StudentInformation student={request?.contactmeta} columnConfig={getColumnConfig.data[0]}/>
        }
    </>
}

function CampaignInfo({surveyDate, data}) {
    const title = data?.config?.title
    const campaign = data?.config?.campaign

    return <>
        <p>
            {campaign && campaign.length > 0 ? (
                <>
                    {campaign} <em>– {title}</em>
                </>
            ) : (
                title
            )}
        </p>
        <p><em>{surveyDate}</em></p>
    </>
}

function StudentInformation({student, columnConfig}) {
    const intl = useIntl()
    const defaultColumns = Object.keys(columnConfig).filter(key => columnConfig[key]?.appVisible).sort((a,b)=>columnConfig[a]?.order - columnConfig[b]?.order)
    const [studentInfo, setStudentInfo] = useState(
        localStorage.getItem("contactMetaSelections") ?
            JSON.parse(localStorage.getItem("contactMetaSelections")) :
            defaultColumns
    )

    return <ul className={"student-list"}>
        {studentInfo.sort((a,b)=>columnConfig[a]?.i - columnConfig[b]?.i).map((e,i) => {
            if (student.hasOwnProperty(e)) {
                return <li key={i}>
                    <div className={"header"}>
                        {intl.formatMessage({
                            id: `contactmeta.${e}`,
                            defaultMessage: e
                        })}
                    </div>
                    <div className={"info"}>{student[e] ? student[e] :
                        <em>{intl.formatMessage({
                            id: 'missingInfo',
                            defaultMessage: 'Missing info',
                        })}</em>}</div>
                </li>
            }
            return null
        })}
    </ul>
}

function SupportRequestsSkeleton() {
    const intl = useIntl()
    return <div className={"support-requests-container"}>
        <input type={"search"} disabled
               placeholder={intl.formatMessage({
                   id: 'loading',
                   defaultMessage: 'Loading',
               })+"..."} />
        <div className={"support-requests"}>
            {
                [...Array(9)].map( (e, i) => {
                    return <div className={"support-request"} key={i}>
                        <div>
                            <Skeleton height={20} width={70} marginBottom={12}/>
                            <Skeleton height={25} width={200} marginBottom={10}/>
                            <Skeleton height={17} width={150}/>
                        </div>
                        <div className={"updated"}>
                            <Skeleton height={35} width={100}/>
                        </div>
                    </div>
                })
            }
        </div>
    </div>
}